/**
 * The ONE way a quantity + unit is printed — on a customer document and on the
 * operator's own screens alike.
 *
 * Why this exists: the same estimate table used to print "1 EA" on one row and
 * "1 each" on the next, because each renderer concatenated whatever raw unit
 * string the line happened to carry — pricebook items store the canonical code
 * ('EA'), while lines built through the item picker / invoice form default to
 * the free-text 'each'. Two spellings of the same unit on one document reads as
 * a bug to the customer.
 *
 * Rules:
 *  - The generic unit ("each", "EA") never prints as a CODE. It either prints
 *    as nothing — "1 EA" becomes "1" — or as the plain word, "1 each", which is
 *    the caller's choice of {@link GenericUnitStyle} and the only thing that
 *    varies between surfaces. Customer documents omit it; operator screens say
 *    it, everywhere, without exception.
 *  - Meaningful units keep their information but in short human words, pluralised
 *    where it reads naturally: "1 hr", "2 hrs", "3 boxes", "40 sq ft".
 *  - A missing/blank unit prints the bare number — never a stray space, never
 *    "undefined".
 *
 * It started customer-only, and the operator's tables kept the raw code on the
 * theory that an operator wants to see what is stored. What they saw was "EA"
 * against every row of a bundle, which is the code for "there is nothing to say
 * here" — noise carrying no information at all. One rule, both audiences.
 *
 * The exceptions are the surfaces whose SUBJECT is the stored value: the unit
 * SELECTOR (whose "Hour (HR)" labels are deliberately operator-facing), the
 * catalog's Unit column, the CSV import preview and the CSV export. Those print
 * the code, because the code is the thing being chosen, reviewed or written.
 * Nothing here touches stored data.
 */
import { PRICEBOOK_UNIT_OPTIONS, type PricebookUnitDisplay } from '@/lib/constants/pricebookUnits';

/** Canonical code (lowercased) → customer wording. `null` = generic, print nothing. */
const DISPLAY_BY_CODE = new Map<string, PricebookUnitDisplay | null>(
  PRICEBOOK_UNIT_OPTIONS.map((option) => [option.value.toLowerCase(), option.display]),
);

/**
 * Free-text spellings that mean the generic unit. Lines created outside the
 * pricebook default to 'each', so these reach customer documents alongside 'EA'.
 */
const GENERIC_UNITS = new Set(['ea', 'ea.', 'each', 'unit', 'units', 'qty']);

/**
 * Free-text spellings of a MEANINGFUL unit, mapped onto its canonical code.
 * Only spellings that are not already the code or one of its display forms —
 * those two are matched automatically below.
 */
const CODE_BY_ALIAS: Readonly<Record<string, string>> = {
  hour: 'HR',
  hours: 'HR',
  foot: 'FT',
  feet: 'FT',
  pound: 'LB',
  pounds: 'LB',
  ounce: 'OZ',
  ounces: 'OZ',
  kilogram: 'KG',
  kilograms: 'KG',
  gallon: 'GAL',
  gallons: 'GAL',
  quart: 'QT',
  quarts: 'QT',
  liter: 'L',
  liters: 'L',
  inch: 'IN',
  inches: 'IN',
  yard: 'YD',
  yards: 'YD',
  meter: 'M',
  meters: 'M',
  'square foot': 'SQFT',
  'square feet': 'SQFT',
  'sq ft': 'SQFT',
  'sq. ft.': 'SQFT',
  'square yard': 'SQYD',
  'square yards': 'SQYD',
  'linear foot': 'LF',
  'linear feet': 'LF',
  'cubic foot': 'CUFT',
  'cubic feet': 'CUFT',
};

/** Display wordings ('hrs', 'boxes', …) resolved back to their code. */
const CODE_BY_DISPLAY_FORM = new Map<string, string>();
for (const option of PRICEBOOK_UNIT_OPTIONS) {
  if (!option.display) continue;
  for (const form of [option.display.one, option.display.other]) {
    const key = form.toLowerCase();
    if (!CODE_BY_DISPLAY_FORM.has(key)) CODE_BY_DISPLAY_FORM.set(key, option.value);
  }
}

function toNumber(quantity: unknown): number {
  const n = typeof quantity === 'number' ? quantity : Number(quantity);
  return Number.isFinite(n) ? n : 0;
}

/**
 * The number itself, normalised: no trailing zeros ("2.00" → "2"), decimals
 * kept ("2.5" stays "2.5"), anything unparseable printed as "0" rather than
 * "NaN" or "undefined".
 */
export function formatQuantityValue(quantity: unknown): string {
  return String(toNumber(quantity));
}

/**
 * What to do with the GENERIC unit — the one that says nothing.
 *
 * `'omit'` prints nothing at all. That is the CUSTOMER document, which was
 * reviewed and accepted reading "1" rather than "1 each".
 *
 * `'word'` prints "each". That is every OPERATOR surface, uniformly — under a
 * column headed Qty as much as inline in a sentence.
 *
 * The word is deliberately "each" and not "unit": `EA` is real trade shorthand
 * that a parts counter or a tech reads instantly, but it is still jargon, and
 * "each" is simply what it abbreviates. It is the plainer word for the same
 * thing, and it needs no glossary.
 *
 * The two are a presentation choice about ONE rule, which is why they are an
 * option here rather than a second helper: a surface picks how the generic case
 * reads and gets every other unit's wording identical to everywhere else.
 */
export type GenericUnitStyle = 'omit' | 'word';

/**
 * "each" at any quantity — 1 each, 3 each. English does not pluralise it, and
 * inventing "eaches" would be the sort of thing that gets read as a bug.
 */
const GENERIC_WORD = 'each';

function genericLabel(style: GenericUnitStyle): string {
  return style === 'word' ? GENERIC_WORD : '';
}

/**
 * The readable wording for a unit at a given quantity, or `''` when nothing
 * should print.
 *
 * A BLANK unit prints nothing under either style: there is no unit, which is
 * not the same statement as "each". Only a unit that says "each" in one of its
 * spellings is the generic one, and `style` decides how that reads.
 *
 * An unrecognised unit is passed through rather than dropped — a tenant's own
 * wording ("zone", "visit") still carries information. All-caps free text is
 * lowercased, since a bare code shouts wherever it is read.
 */
export function unitLabel(
  unit: string | null | undefined,
  quantity: unknown,
  style: GenericUnitStyle = 'omit',
): string {
  const raw = typeof unit === 'string' ? unit.trim() : '';
  if (!raw) return '';

  const key = raw.toLowerCase();
  if (GENERIC_UNITS.has(key)) return genericLabel(style);

  const code = DISPLAY_BY_CODE.has(key)
    ? key
    : (CODE_BY_DISPLAY_FORM.get(key) ?? CODE_BY_ALIAS[key])?.toLowerCase();

  const display = code ? DISPLAY_BY_CODE.get(code) : undefined;
  // Resolved to the generic unit — a catalog code whose display is deliberately
  // null, such as 'EA'.
  if (display === null) return genericLabel(style);
  if (display) return Math.abs(toNumber(quantity)) === 1 ? display.one : display.other;

  // Unknown unit: keep the tenant's word, but don't shout a raw code.
  return raw === raw.toUpperCase() ? raw.toLowerCase() : raw;
}

/**
 * Quantity as it should read in a Qty column: `1 EA` → `1`, `1 each` → `1`,
 * `2.5 HR` → `2.5 hrs`, `3 BOX` → `3 boxes`.
 *
 * Pass `'word'` — every operator surface does — where the generic unit should
 * read as "1 each" instead of being dropped. See {@link GenericUnitStyle}.
 */
export function formatQuantityWithUnit(
  quantity: unknown,
  unit?: string | null,
  style: GenericUnitStyle = 'omit',
): string {
  const value = formatQuantityValue(quantity);
  const label = unitLabel(unit, quantity, style);
  return label ? `${value} ${label}` : value;
}
